import { useCallback, useEffect, useState } from "react";

export type CopyState = "idle" | "copied" | "failed";
const RESET_MS = 2_000;

/** Browsers without the async clipboard (older WebViews, some embedded panes): select-and-copy through a scratch textarea. */
function legacyCopy(value: string): boolean {
  const area = document.createElement("textarea");
  area.value = value;
  area.setAttribute("readonly", "");
  area.className = "sr-only";
  document.body.appendChild(area);
  area.select();
  let ok = false;
  try {
    ok = document.execCommand("copy");
  } finally {
    area.remove();
  }
  return ok;
}

/** Copy a string and say what happened: `copied` or `failed` for two seconds, then back to `idle`. */
export function useCopyToClipboard(): { state: CopyState; copy: (value: string) => Promise<void> } {
  const [state, setState] = useState<CopyState>("idle");

  useEffect(() => {
    if (state === "idle") return;
    const t = setTimeout(() => setState("idle"), RESET_MS);
    return () => clearTimeout(t);
  }, [state]);

  const copy = useCallback(async (value: string) => {
    try {
      if (typeof navigator.clipboard?.writeText === "function") {
        await navigator.clipboard.writeText(value);
      } else if (!legacyCopy(value)) {
        throw new Error("clipboard unavailable");
      }
      setState("copied");
    } catch {
      setState("failed");
    }
  }, []);

  return { state, copy };
}
